import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Paper,
	Typography,
} from '@mui/material';

import { CANCELLATION_DEADLINE_MINUTES } from '@common/constants/academyReworkings';
import { bookingLessonName, parseDate, teacherName } from '@common/helpers/academyReworkings';
import { BookedMeet } from '@common/types/academyReworkings';

import useMyBookings from '../../hooks/useMyBookings';
import { cardSx, nextButtonSx } from '../../styled';
import BookingDetails from '../BookingDetails';
import ErrorNotice from '../ErrorNotice';

function MyBookings({
	studentId,
	onCancelled,
}: {
	studentId?: number;
	onCancelled?: () => Promise<unknown>;
}) {
	const { t } = useTranslation();
	const [details, setDetails] = useState<BookedMeet | null>(null);
	const state = useMyBookings(studentId, t('academyReworkings.requestFailed'), onCancelled);
	const deadline = { minutes: CANCELLATION_DEADLINE_MINUTES };
	const targetName = state.target
		? bookingLessonName(state.target, t('academyReworkings.main'))
		: '';
	return (
		<Box display="flex" flexDirection="column" gap="16px">
			<Box>
				<Typography variant="h3">{t('academyReworkings.myBookings')}</Typography>
				<Typography color="text.secondary" sx={{ mt: 1 }}>
					{t('academyReworkings.cancellationHint', deadline)}
				</Typography>
			</Box>
			{!studentId && <Alert severity="info">{t('academyReworkings.studentRequired')}</Alert>}
			{state.error && <ErrorNotice message={state.error} onRetry={state.retry} />}
			{state.loading && (
				<Box display="flex" justifyContent="center" py={4}>
					<CircularProgress />
				</Box>
			)}
			{!state.loading && !state.error && studentId && !state.bookings.length && (
				<Paper sx={cardSx}>
					<Typography color="text.secondary">{t('academyReworkings.noBookings')}</Typography>
				</Paper>
			)}
			{state.bookings.map((booking) => {
				const lessonName = bookingLessonName(booking, t('academyReworkings.main'));
				return (
					<Paper
						key={booking.id}
						sx={(theme) => ({
							...cardSx(theme),
							display: 'grid',
							gridTemplateColumns: 'minmax(0, 1fr)',
							gap: '16px',
							alignItems: 'center',
							[theme.breakpoints.up('sm')]: {
								p: 3,
								gridTemplateColumns: '150px minmax(0, 1fr) auto',
							},
						})}
					>
						<Box>
							<Typography variant="h4">
								{parseDate(booking.start_dt).toFormat('HH:mm')}–
								{parseDate(booking.end_dt).toFormat('HH:mm')}
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
								{parseDate(booking.start_dt).toFormat('ccc, dd.MM.yyyy')}
							</Typography>
						</Box>
						<Box sx={{ minWidth: 0 }}>
							<Typography fontWeight={600}>{lessonName}</Typography>
							{booking.teacher && (
								<Typography color="text.secondary" sx={{ mt: 0.5 }}>
									{t('academyReworkings.tutor')}: {teacherName(booking.teacher)}
								</Typography>
							)}
						</Box>
						<Box
							sx={(theme) => ({
								display: 'grid',
								gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
								gap: '8px',
								minWidth: 0,
								[theme.breakpoints.up('sm')]: { gridTemplateColumns: 'auto auto' },
							})}
						>
							<Button
								variant="outlined"
								size="small"
								aria-label={`${t('academyReworkings.viewBooking')}: ${lessonName}`}
								sx={(theme) => ({ ...nextButtonSx(theme), px: 1.5, fontSize: '14px' })}
								startIcon={<InfoOutlinedIcon />}
								disabled={state.cancelling}
								onClick={() => setDetails(booking)}
							>
								{t('academyReworkings.viewBooking')}
							</Button>
							<Button
								variant="outlined"
								size="small"
								aria-label={`${t('academyReworkings.cancelBooking')}: ${lessonName}`}
								sx={(theme) => ({ ...nextButtonSx(theme), px: 1.5, fontSize: '14px' })}
								disabled={state.cancelling}
								onClick={() => state.requestCancel(booking)}
							>
								{t('academyReworkings.cancelBooking')}
							</Button>
						</Box>
					</Paper>
				);
			})}
			<Typography variant="body2" color="text.secondary">
				{t('academyReworkings.timeZone')}
			</Typography>
			<BookingDetails booking={details} onClose={() => setDetails(null)} />
			<Dialog
				open={!!state.target}
				onClose={state.closeDialog}
				fullWidth
				maxWidth="sm"
				aria-labelledby="academy-cancel-title"
				PaperProps={{
					sx: {
						maxWidth: '520px',
						width: 'calc(100% - 32px)',
						p: 2,
						m: 2,
						overflowWrap: 'anywhere',
					},
				}}
			>
				<Box display="flex" justifyContent="center" pt={1}>
					{state.blocked ? (
						<AccessTimeOutlinedIcon color="warning" sx={{ fontSize: '40px' }} />
					) : (
						<HelpOutlineIcon color="primary" sx={{ fontSize: '40px' }} />
					)}
				</Box>
				<DialogTitle id="academy-cancel-title" sx={{ textAlign: 'center' }}>
					{t(
						state.blocked
							? 'academyReworkings.cancellationUnavailable'
							: 'academyReworkings.cancelTitle',
					)}
				</DialogTitle>
				<DialogContent sx={{ minWidth: 0 }}>
					<Typography textAlign="center" color="text.secondary">
						{state.blocked
							? t('academyReworkings.cancellationBlocked', deadline)
							: t('academyReworkings.cancelDescription', {
									lesson: targetName,
									date: state.target
										? parseDate(state.target.start_dt).toFormat('dd.MM.yyyy, HH:mm')
										: '',
								})}
					</Typography>
					{state.cancelError && (
						<Box mt={2}>
							<ErrorNotice message={state.cancelError} />
						</Box>
					)}
				</DialogContent>
				<DialogActions sx={{ flexWrap: 'wrap', justifyContent: 'center', gap: '8px' }}>
					{!state.blocked && (
						<Button variant="outlined" onClick={state.closeDialog} disabled={state.cancelling}>
							{t('academyReworkings.keepBooking')}
						</Button>
					)}
					<Button
						variant="contained"
						color={state.blocked ? 'primary' : 'error'}
						disabled={state.cancelling}
						onClick={state.blocked ? state.closeDialog : state.confirmCancel}
						startIcon={state.cancelling ? <CircularProgress size={18} /> : undefined}
					>
						{t(state.blocked ? 'academyReworkings.understood' : 'academyReworkings.confirmCancel')}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}

export default observer(MyBookings);
